import { useContext, useState, useMemo } from "react";
import { HouseContext } from "../../../../context/HouseContext";
import { Link } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaStar,
  FaPlusCircle,
  FaSistrix,
  FaRegListAlt,
  FaBed,
  FaBath,
  FaFilter,
  FaTimes,
  FaEye,
  FaChalkboard,
} from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa6";
import { deleteProperty } from "../../../../application/deleteProperty";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import {
  getStatusClasses,
  getTypeBadgeClasses,
} from "../../../../utils/propertyStatusColors";

const PAGE_SIZE = 12;

function LazyImage({ src, alt, className }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />
      )}
      {error ? (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center rounded">
          <span className="text-gray-400 text-xs">Sin imagen</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`w-full h-full object-cover rounded transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}

function PropertyList() {
  const { allHouses, removeHouse } = useContext(HouseContext);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [propertyIdToDelete, setPropertyIdToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const houses = useMemo(() => allHouses || [], [allHouses]);

  // Extract unique values for filters
  const types = useMemo(() => [...new Set(houses.map((h) => h.type))].sort(), [houses]);
  const states = useMemo(() => [...new Set(houses.map((h) => h.state))].sort(), [houses]);
  const locations = useMemo(() => [...new Set(houses.map((h) => h.country))].sort(), [houses]);

  const filteredHouses = useMemo(() => {
    return houses.filter((house) => {
      const matchesSearch = house.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        house.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        house.address?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !filterType || house.type === filterType;
      const matchesState = !filterState || house.state === filterState;
      const matchesLocation = !filterLocation || house.country === filterLocation;
      return matchesSearch && matchesType && matchesState && matchesLocation;
    });
  }, [houses, searchTerm, filterType, filterState, filterLocation]);

  const totalPages = Math.ceil(filteredHouses.length / PAGE_SIZE);
  const paginatedHouses = filteredHouses.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const activeFilters = [filterType, filterState, filterLocation].filter(Boolean).length;

  const clearFilters = () => {
    setFilterType("");
    setFilterState("");
    setFilterLocation("");
    setSearchTerm("");
    setCurrentPage(0);
  };

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await deleteProperty(propertyIdToDelete);
      removeHouse(propertyIdToDelete);
      setIsConfirmOpen(false);
      setPropertyIdToDelete(null);
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
      alert("Error al eliminar la propiedad. Por favor, inténtalo de nuevo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPropertyIdToDelete(null);
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        message="¿Estás seguro de que quieres eliminar esta propiedad?"
        isDeleting={isDeleting}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Propiedades</h1>
          <span className="text-sm bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-semibold">
            {filteredHouses.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard/propiedades/agregar"
            className="inline-flex items-center gap-1.5 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <FaPlusCircle className="w-4 h-4" />
            Agregar
          </Link>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === "list"
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            <FaRegListAlt className="text-lg" />
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === "cards"
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            <FaClipboardList className="text-lg" />
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <FaSistrix className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            placeholder="Buscar por nombre, localidad o direccion..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        {/* Filters row */}
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
            <FaFilter className="w-3 h-3" />
            <span className="text-xs font-medium">Filtros:</span>
          </div>
          <div className="flex flex-wrap gap-2 flex-1">
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCurrentPage(0); }}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Todos los tipos</option>
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              value={filterState}
              onChange={(e) => { setFilterState(e.target.value); setCurrentPage(0); }}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Todos los estados</option>
              {states.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filterLocation}
              onChange={(e) => { setFilterLocation(e.target.value); setCurrentPage(0); }}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Todas las localidades</option>
              {locations.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            {activeFilters > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1.5"
              >
                <FaTimes className="w-3 h-3" />
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {filteredHouses.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <p className="text-gray-400 dark:text-gray-500 text-lg">No se encontraron propiedades</p>
          {activeFilters > 0 && (
            <button onClick={clearFilters} className="mt-2 text-sm text-primary hover:underline">
              Limpiar filtros
            </button>
          )}
        </div>
      ) : viewMode === "list" ? (
        /* ---- TABLE VIEW ---- */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3">Propiedad</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3">Localidad</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3">Tipo</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3">Estado</th>
                  <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3 w-16">Dest.</th>
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-3 w-24">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginatedHouses.map((house) => (
                  <tr key={house.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <LazyImage
                          src={house.images?.[0]}
                          alt={house.name}
                          className="w-14 h-10 flex-shrink-0 rounded overflow-hidden"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate capitalize">
                            {house.name}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{house.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">{house.country}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getTypeBadgeClasses(house.type)}`}>
                        {house.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusClasses(house.state)}`}>
                        {house.state}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {house.highlights ? (
                        <FaStar className="text-yellow-400 w-4 h-4 mx-auto" />
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`/propiedad/${house.id}?vista=publica`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                          title="Ver la propiedad publicada"
                        >
                          <FaEye className="w-3.5 h-3.5" />
                        </a>
                        <Link
                          to={`/dashboard/propiedades/cartel/${house.id}`}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                          title="Imprimir cartel de la propiedad"
                        >
                          <FaChalkboard className="w-3.5 h-3.5" />
                        </Link>
                        <Link
                          to={`/dashboard/propiedades/editar/${house.id}`}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                        >
                          <FaPencilAlt className="w-3.5 h-3.5" />
                        </Link>
                        <button
                          onClick={() => {
                            setPropertyIdToDelete(house.idFirebase);
                            setIsConfirmOpen(true);
                          }}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-500 transition-colors"
                          disabled={isDeleting}
                        >
                          <FaTrashAlt className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {currentPage * PAGE_SIZE + 1}-{Math.min((currentPage + 1) * PAGE_SIZE, filteredHouses.length)} de {filteredHouses.length}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ---- CARDS VIEW ---- */
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginatedHouses.map((house) => (
              <div
                key={house.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
              >
                {/* Image */}
                <div className="relative">
                  <LazyImage
                    src={house.images?.[0]}
                    alt={house.name}
                    className="w-full h-48"
                  />
                  <span
                    className={`absolute top-2 left-2 text-xs font-semibold px-2 py-1 rounded-full ${getStatusClasses(house.state)}`}
                  >
                    {house.state}
                  </span>
                  {house.highlights && (
                    <span className="absolute top-2 right-2 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                      <FaStar className="w-3 h-3" /> Destacada
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 capitalize truncate">
                      {house.name}
                    </h3>
                    {house.price && (
                      <span className="text-sm font-bold text-primary whitespace-nowrap ml-2">
                        {house.currency || "$"}{Number(house.price).toLocaleString("es-AR")}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 truncate">
                    {house.country} - {house.address}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${getTypeBadgeClasses(house.type)}`}
                    >
                      {house.type}
                    </span>
                    {house.characteristics?.dormitorios && (
                      <span className="flex items-center gap-1">
                        <FaBed className="w-3.5 h-3.5" /> {house.characteristics.dormitorios}
                      </span>
                    )}
                    {house.characteristics?.baños && (
                      <span className="flex items-center gap-1">
                        <FaBath className="w-3.5 h-3.5" /> {house.characteristics.baños}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <a
                      href={`/propiedad/${house.id}?vista=publica`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-600 dark:text-gray-300 hover:text-primary flex items-center gap-1"
                    >
                      <FaEye className="w-3 h-3" /> Ver
                    </a>
                    <Link
                      to={`/dashboard/propiedades/cartel/${house.id}`}
                      className="text-sm text-gray-600 dark:text-gray-300 hover:text-primary flex items-center gap-1"
                      title="Imprimir cartel de la propiedad"
                    >
                      <FaChalkboard className="w-3 h-3" /> Cartel
                    </Link>
                    <Link
                      to={`/dashboard/propiedades/editar/${house.id}`}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <FaPencilAlt className="w-3 h-3" /> Editar
                    </Link>
                    <button
                      onClick={() => {
                        setPropertyIdToDelete(house.idFirebase);
                        setIsConfirmOpen(true);
                      }}
                      className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                      disabled={isDeleting}
                    >
                      <FaTrashAlt className="w-3 h-3" /> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {currentPage * PAGE_SIZE + 1}-{Math.min((currentPage + 1) * PAGE_SIZE, filteredHouses.length)} de {filteredHouses.length}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PropertyList;
